'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/lib/contexts/auth-context'
import { NotificationProvider, NotificationToast } from '@/lib/contexts/notification-context'
import { ThemeProvider, useTheme } from '@/lib/contexts/theme-context'
import { Toaster } from '@/components/ui/toaster'
import { Theme } from '@radix-ui/themes'
import '@radix-ui/themes/styles.css'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (previously cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function ThemedApp({ children }: { children: React.ReactNode }) {
  const { resolvedTheme } = useTheme()

  return (
    <Theme
      accentColor="blue"
      grayColor="gray"
      radius="medium"
      scaling="100%"
      panelBackground="solid"
      appearance={resolvedTheme}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <NotificationProvider>
            {children}
            <NotificationToast />
            <Toaster />
          </NotificationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Theme>
  )
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider>
      <ThemedApp>{children}</ThemedApp>
    </ThemeProvider>
  )
}
